'use client'

import { useEffect, useState } from 'react'
import { useRouter } from 'next/navigation'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAuth } from '../context/AuthContext'
import { StorageEnum } from '../enum/storage.enum'
import {
  decodeJwtPayload,
  getPostLoginRedirectPath,
  JwtOrganization,
  JwtPayload,
} from '@/lib/jwt'

export default function LoginPage() {
  const { login, completeLoginSession, logout } = useAuth()!
  const router = useRouter()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [checkingSession, setCheckingSession] = useState(true)

  const [step, setStep] = useState<'login' | 'org-picker'>('login')
  const [jwtPayload, setJwtPayload] = useState<JwtPayload | null>(null)
  const [accessToken, setAccessToken] = useState('')
  const [authId, setAuthId] = useState<string | undefined>()
  const [organizations, setOrganizations] = useState<JwtOrganization[]>([])
  const [selectedOrgId, setSelectedOrgId] = useState('')

  useEffect(() => {
    const token = localStorage.getItem(StorageEnum.userToken)
    const storedUser = localStorage.getItem(StorageEnum.userData)

    if (token && storedUser) {
      const user = JSON.parse(storedUser)
      router.replace(getPostLoginRedirectPath(user))
      return
    }

    setCheckingSession(false)
  }, [router])

  const finishLogin = (
    token: string,
    payload: JwtPayload,
    org: JwtOrganization | undefined,
    id?: string
  ) => {
    const user = completeLoginSession(token, payload, org, id)
    router.push(getPostLoginRedirectPath(user))
  }

  const handleOrgContinue = () => {
    if (!jwtPayload || !accessToken || !selectedOrgId) return

    const org = organizations.find((o) => o.organizationId === selectedOrgId)
    if (!org) {
      setError('Please select an organization')
      return
    }

    setLoading(true)
    setError('')
    finishLogin(accessToken, jwtPayload, org, authId)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const res = await login(email, password)
      if (!res.access_token) {
        throw new Error('Login failed')
      }

      const payload = decodeJwtPayload(res.access_token)
      const orgs = (payload.organizations ?? []) as JwtOrganization[]

      setAccessToken(res.access_token)
      setAuthId(res.authId)
      setJwtPayload(payload)

      if (orgs.length > 1) {
        setOrganizations(orgs)
        setSelectedOrgId(orgs[0].organizationId)
        setStep('org-picker')
        setLoading(false)
        return
      }

      finishLogin(res.access_token, payload, orgs[0], res.authId)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Login failed'
      setError(message)
      setLoading(false)
      logout()
    }
  }

  if (checkingSession) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-full max-w-md space-y-6">
        <h1 className="text-4xl font-bold text-center bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
          Cluster
        </h1>
        <Card className="w-full">
          <CardHeader>
            <CardTitle className="text-center text-2xl">
              {step === 'login' ? 'Sign in' : 'Select organization'}
            </CardTitle>
            <CardDescription className="text-center">
              {step === 'login'
                ? 'Enter your email below to sign in to your account'
                : 'Choose the organization you want to access'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {step === 'login' ? (
              <form className="space-y-4" onSubmit={handleSubmit}>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    autoComplete="email"
                    placeholder="Enter your email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    required
                  />
                </div>
                {error && <p className="text-sm text-red-500">{error}</p>}
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? 'Signing in...' : 'Sign in'}
                </Button>
              </form>
            ) : (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="organization">Organization</Label>
                  <Select value={selectedOrgId} onValueChange={setSelectedOrgId}>
                    <SelectTrigger id="organization">
                      <SelectValue placeholder="Select organization" />
                    </SelectTrigger>
                    <SelectContent>
                      {organizations.map((org) => (
                        <SelectItem
                          key={org.organizationId}
                          value={org.organizationId}
                        >
                          {org.organizationName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {error && <p className="text-sm text-red-500">{error}</p>}
                <Button
                  type="button"
                  className="w-full"
                  disabled={loading || !selectedOrgId}
                  onClick={handleOrgContinue}
                >
                  {loading ? 'Continuing...' : 'Continue'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
